import { useEffect, useState, useCallback } from "react";
import { Autocomplete, Box, CircularProgress, Grid, Stack, TextField } from "@mui/material";
import ApiService from "../../../utils/fetch";
import { PowerBIEmbed } from "powerbi-client-react";
import { models } from "powerbi-client";
import { styled } from "@mui/material/styles";
import { useSelector } from "react-redux";

// Styled components
const PillContainer = styled("div")(() => ({
    position: "relative",
    display: "inline-flex",
    borderRadius: 20,
    background: "#f5f5f5",
    height: 32,
    minWidth: 550,
    alignItems: "center",
    boxShadow: "0 0 0 1px #ddd",
}));

const PillHighlight = styled("div")(({ selected }) => ({
    position: "absolute",
    top: 2,
    left: selected * (100 / 2) + "%",
    width: `calc(${100 / 2}% - 4px)`,
    height: "calc(100% - 4px)",
    background: "#222",
    borderRadius: 16,
    transition: "left 0.25s cubic-bezier(.4,0,.2,1)",
    zIndex: 1,
}));

const PillLabel = styled("div")(({ active }) => ({
    position: "relative",
    zIndex: 2,
    flex: 1,
    textAlign: "center",
    fontSize: "0.95rem",
    fontWeight: active ? 600 : 400,
    color: active ? "#fff" : "#222",
    cursor: "pointer",
    minWidth: 140,
    padding: "0 12px",
    userSelect: "none",
    transition: "color 0.2s",
    lineHeight: "28px",
}));

//Reports Moved to Premium Workspace in power BI
const Reports = () => {
    const [selectedChartTab, setSelectedChartTab] = useState(0);
    const [loading, setLoading] = useState(true);
    const [embedConfig, setEmbedConfig] = useState(null);
    const [reportConfigs, setReportConfigs] = useState([null, null]);
    const dataElementsOptions = ["Bottler Concentrate Forecast", "Bottler Market Forecast"];
    const [selectedDataElement, setSelectedDataElement] = useState("Bottler Concentrate Forecast");
    const userReportPages = {
        "Bottler Forecast": process.env.REACT_APP_USER_PAGE_NAME,
        "Bottler Market Forecast": process.env.REACT_APP_MF_USER_PAGE_NAME,
    };

    const groupId = process.env.REACT_APP_POWER_BI_GROUP_ID;
    const userId = useSelector((state) => state.user?.userData?.id);
    const roles = useSelector((state) => state.user?.roles) || [];
    console.log("Roles:", roles);

    // ---- Chart tabs ----
    const chartTabs = [
        {
            label: "User Report",
            reportId: process.env.REACT_APP_USER_REPORT_ID,
            pageName: process.env.REACT_APP_USER_PAGE_NAME,
            table: "vw_user_report",
            buildFilter: (userId, roles) => {
                const safeRoles = Array.isArray(roles) ? roles : [];
                // Admin bypasses filters
                if (safeRoles.includes("Admin")) return [];
                return [
                    {
                        $schema: "http://powerbi.com/product/schema#basic",
                        target: { table: "vw_user_report", column: "userId" },
                        operator: "Equals",
                        values: [userId],
                        filterType: models.FilterType.BasicFilter,
                    },
                ];
            }, buildFilter: (userId, roles, selectedPage) => {
                const safeRoles = Array.isArray(roles) ? roles : [];

                if (safeRoles.includes("Admin")) return [];

                const tableName =
                    selectedPage === process.env.REACT_APP_USER_PAGE_NAME
                        ? "vw_user_report"
                        : "vw_user_profit_center_access_report";

                return [
                    {
                        $schema: "http://powerbi.com/product/schema#basic",
                        target: { table: tableName, column: "user_role" },
                        operator: "NotIn",
                        values: ["Admin"],
                        filterType: models.FilterType.BasicFilter,
                    },
                ];
            }
        },
        {
            label: "Config Report",
            reportId: process.env.REACT_APP_CONFIG_REPORT_ID,
            pageName: process.env.REACT_APP_CONFIG_PAGE_NAME,
            table: "vw_config_report",
            buildFilter: (userId, roles) => {
                return [
                    {
                        $schema: "http://powerbi.com/product/schema#basic",
                        target: { table: "vw_config_report", column: "userId" },
                        operator: "Equals",
                        values: [userId],
                        filterType: models.FilterType.BasicFilter,
                    },
                ];
            },
        },
    ];

    // ---- Build PowerBI config ----
    const buildConfig = useCallback(
        (response, { pageName, buildFilter }) => ({
            type: "report",
            id: response?.data?.reportId,
            embedUrl: `${response?.data?.embedUrl.split("&pageName=")[0]}&pageName=${pageName}`,
            accessToken: response?.data?.accessToken,
            tokenType: models.TokenType.Embed,
            settings: {
                panes: { filters: { expanded: false, visible: false } },
                navContentPaneEnabled: false,
                layoutType: models.LayoutType.Custom,
                customLayout: { displayOption: models.DisplayOption.FitToWidth },
            },
            filters: buildFilter(userId, roles, pageName),
        }),
        [userId, roles]
    );

    const fetchReportConfig = useCallback(
        async (tab) => {
            const response = await ApiService.get(
                `v1/powerBI/embedDetails/group/${groupId}/report/${tab.reportId}`
            );
            return buildConfig(response, tab);
        },
        [groupId, buildConfig]
    );

    useEffect(() => {
        // Wait until roles are available before fetching
        if (!Array.isArray(roles) || roles.length === 0) return;

        let isMounted = true;
        const loadReports = async () => {
            try {
                setLoading(true);
                const [userConfig, configConfig] = await Promise.all([
                    fetchReportConfig(chartTabs[0]),
                    fetchReportConfig(chartTabs[1]),
                ]);
                if (isMounted) {
                    setReportConfigs([userConfig, configConfig]);
                    setEmbedConfig(userConfig);
                }
            } catch (error) {
                console.error("API error:", error);
            } finally {
                if (isMounted) setLoading(false);
            }
        };
        loadReports();

        return () => {
            isMounted = false;
        };
    }, [fetchReportConfig, roles]);

    useEffect(() => {
        setEmbedConfig(reportConfigs[selectedChartTab] || null);
    }, [selectedChartTab, reportConfigs]);

    useEffect(() => {
        if (!reportConfigs[0]) return;

        if (selectedChartTab === 0) {
            const selectedPage = userReportPages[selectedDataElement];

            setEmbedConfig(prev => ({
                ...prev,
                embedUrl: `${prev.embedUrl.split("&pageName=")[0]}&pageName=${selectedPage}`
            }));
        }
    }, [selectedDataElement, selectedChartTab]);

    return (
        <Stack>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <div style={{ marginTop: "10px" }}>
                        <Box sx={{ m: 1, display: "flex", gap: 2 }}>
                            <PillContainer>
                                <PillHighlight selected={selectedChartTab} />
                                {chartTabs.map((tab, idx) => (
                                    <PillLabel
                                        key={tab.label}
                                        active={selectedChartTab === idx}
                                        onClick={() => setSelectedChartTab(idx)}
                                        style={{
                                            fontWeight: selectedChartTab === idx ? 600 : 400,
                                        }}
                                    >
                                        {tab.label}
                                    </PillLabel>
                                ))}
                            </PillContainer>
                        </Box>

                        {selectedChartTab === 0 && (<Grid item xs={12} sm={6} md={3} sx={{ mt: 2, mb: 1 }}>
                            <Autocomplete
                                disablePortal
                                options={dataElementsOptions}
                                size="small"
                                value={selectedDataElement}
                                onChange={(e, v) => setSelectedDataElement(v)}
                                sx={{ width: 250 }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Data Elements*"
                                        InputLabelProps={{
                                            shrink: true,
                                            style: {
                                                transform: "translate(14px, -14px) scale(0.75)",
                                            },
                                        }}
                                    />
                                )}
                            />
                        </Grid>)}

                        {loading ? (
                            <Box
                                sx={{
                                    width: "100%",
                                    height: "40vh",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <CircularProgress color="inherit" />
                            </Box>
                        ) : (
                            <Box sx={{ flex: 1, p: 1 }}>
                                {embedConfig?.embedUrl && embedConfig?.accessToken && (
                                    <PowerBIEmbed
                                        embedConfig={embedConfig}
                                        cssClassName="power-bi-report-height"
                                    />
                                )}
                            </Box>
                        )}
                    </div>
                </Grid>
            </Grid>
        </Stack>
    );
};

export default Reports;
